import { useCallback, useState } from 'react';

import { Nullable, SelectOption, SelectOptionWithImage } from '~globals/types';
import { VehicleBase } from '~services/vehicle/types';
import { DriverCompanyBase } from '~services/driver/types';
import { LogisticZoneBase } from '~services/logisticsZones/types';
import { RouteUserBasic } from '~services/route/types';
import { AuthExtendedCompany } from '~services/auth/types';

import { FiltersValuesReturn, useFiltersValues } from './useFiltersValues';

export interface ItemsSearchFilters {
  // COMMONS
  search: string;
  expirationDateTimeFrom: Nullable<Date>;
  expirationDateTimeTo: Nullable<Date>;
  includeWithoutExpirationDate: Nullable<boolean>;
  dateTo: Nullable<Date>;
  dateFrom: Nullable<Date>;
  // SPECIFIC ITEMS
  finalizedDateTimeFrom: Nullable<Date>;
  finalizedDateTimeTo: Nullable<Date>;
  unit1Gt: string;
  unit1Lt: string;
  unit2Gt: string;
  unit2Lt: string;
  priceGt: string;
  priceLt: string;
  vehicles: Nullable<VehicleBase[]>;
  drivers: Nullable<DriverCompanyBase[]>;
  services: Nullable<SelectOption[]>;
  zones: Nullable<LogisticZoneBase[]>;
  assignedUser: Nullable<RouteUserBasic>;
  sourceImports: Nullable<SelectOptionWithImage[]>;
  itemState: Nullable<SelectOption[]>;
  pickingState: Nullable<SelectOption>;
  // SPECIFIC COLLECT
  giverCompany: Nullable<AuthExtendedCompany>;
  collectState: Nullable<SelectOption[]>;
  giverCollectState: Nullable<SelectOption[]>;
}

export interface ItemsSearchFiltersReturn
  extends FiltersValuesReturn<ItemsSearchFilters> {
  filtersActives: ItemsSearchFilters;
  setFiltersActives: (newFiltersActives: ItemsSearchFilters) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFilterActiveByKey: (key: string, newVal: any) => void;
  clearFiltersActives: (newFiltersActives?: ItemsSearchFilters) => void;
}

export const useItemsSearchFilters = (
  initialFilters: ItemsSearchFilters,
): ItemsSearchFiltersReturn => {
  const filters = useFiltersValues<ItemsSearchFilters>(initialFilters);

  const [filtersActives, setFiltersActives] = useState<ItemsSearchFilters>(
    filters.filters,
  );

  const setFilterActiveByKey = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: string, newVal: any) => {
      setFiltersActives((prevActiveFilters) => ({
        ...prevActiveFilters,
        [key]: newVal,
      }));
    },
    [],
  );

  const clearFiltersActives = useCallback(
    (newFiltersActives?: ItemsSearchFilters) => {
      setFiltersActives(newFiltersActives ?? initialFilters);
    },
    [initialFilters],
  );

  return {
    ...filters,
    filtersActives,
    setFiltersActives,
    setFilterActiveByKey,
    clearFiltersActives,
  };
};
